import { LoadingIconComponent } from '../@icons/loading-icon.component';
import { Component, OnInit, inject, ChangeDetectionStrategy } from '@angular/core';
import { DataStore } from '../@commons/@services/data/data-store.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';
import { SharedModule } from '../@commons/ui-components/shared.module';
import { fadeIn } from '../@commons/animations/animations';
import { UserApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/user/user-api';
import { User } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@entities/app/user.entity';
import { Address } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@entities/app/address.entity';
import { AddressApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/location/address/address-api';
import { AddressToUserApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/location/address-to-user/address-to-user-api';
import { AddressToUserListRequest } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/requests/location/address-to-user-list.request';
import { StorageService } from '../@commons/@services/front/storage.service';
import { AddressListRequest } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/requests/location/address-list.request';
import { UserAddressCardComponent } from './@components/user-address-card/user-address-card.component';
import { AuthApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/auth/auth.api';
import RoutesConstants from '../@commons/constants/routes.constants';
import { EditUserToastComponent } from './@toasts/edit-user-toast/edit-user-toast.component';
import { LanguageToastComponent } from './@toasts/language-toast/language-toast.component';
import { LANGUAGE_NATIVE_NAMES } from '../@commons/@services/front/app-language.service';
import { Language } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@enums/language.enum';
import { NotificationPreferencesToastComponent } from './@toasts/notification-preferences-toast/notification-preferences-toast.component';
import { HeaderPaddingSafeAreaComponent } from '../@commons/@components/header-padding-safe-area/header-padding-safe-area.component';
import { SkeletonLoaderComponent } from '../@commons/@components/loaders/skeleton-loader/skeleton-loader.component';
import { DeleteIconComponent } from '../@icons/delete-icon.component';
import { BellIconComponent } from '../@icons/bell-icon.component';
import { CircleUserIconComponent } from '../@icons/circle-user-icon.component';
import { EarthIconComponent } from '../@icons/earth-icon.component';
import { LogoutIconComponent } from '../@icons/logout-icon.component';
import { MessageCircleQuestionIconComponent } from '../@icons/message-circle-question-icon.component';
import { ShovelIconComponent } from '../@icons/shovel-icon.component';
import { ChevronRightIconComponent } from '../@icons/chevron-right.component';
import { ListRowComponent } from '../@commons/ui-components/list-row/list-row.component';
import { ListSectionComponent } from '../@commons/ui-components/list-row/list-section.component';
import { ConfirmDialogComponent } from '../@commons/ui-components/confirm-dialog/confirm-dialog.component';
import { environment } from '../../environments/environment';
import { ImgComponent } from '../@commons/@components/img/img.component';
import { DeviceTokenService } from '../@commons/@services/webview/device-token.service';
import { PosthogService } from '../@commons/@services/front/posthog.service';
import { PremiumService } from '../@commons/@services/front/premium.service';
import { PremiumToastComponent } from '../@commons/@toasts/premium-toast/premium-toast.component';
import { PremiumUpsellCardComponent } from '../@commons/ui-components/premium-upsell-card/premium-upsell-card.component';
import { DowngradeToastComponent } from '../@commons/@toasts/downgrade-toast/downgrade-toast.component';
import { SubscriptionToastComponent } from './@toasts/subscription-toast/subscription-toast.component';
import { AddressLocationsService } from './address-locations.service';
import { PushPermissionService } from '../@commons/@services/webview/push-permission.service';
import { currentAppLang, formatVwDate } from '../@commons/pipes/vw-date.pipe';
import { VwDatePipe } from '../@commons/pipes/vw-date.pipe';
import { userDisplayName, userInitial } from '../@commons/utils/user-display-name.util';
import { StateComponent } from '../@commons/ui-components/state/state.component';
import { LOADING, LoadState, errorState } from '../@commons/@services/data/load-state';

@Component({
  selector: 'vw-user-profile', // Marks this component as standalone
  imports: [
    LoadingIconComponent,
    SharedModule,
    TranslatePipe,
    CommonModule,
    UserAddressCardComponent,
    EditUserToastComponent,
    LanguageToastComponent,
    NotificationPreferencesToastComponent,
    HeaderPaddingSafeAreaComponent,
    SkeletonLoaderComponent,
    DeleteIconComponent,
    BellIconComponent,
    CircleUserIconComponent,
    EarthIconComponent,
    LogoutIconComponent,
    MessageCircleQuestionIconComponent,
    ShovelIconComponent,
    ChevronRightIconComponent,
    ListRowComponent,
    ListSectionComponent,
    ConfirmDialogComponent,
    ImgComponent,
    PremiumToastComponent,
    PremiumUpsellCardComponent,
    SubscriptionToastComponent,
    DowngradeToastComponent,
    VwDatePipe,
    StateComponent,
  ], // Modules and components imported for use in this component
  templateUrl: './user-profile.component.html', // HTML template for the component
  styleUrl: './user-profile.component.scss', // Stylesheet for the component
  animations: [fadeIn],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    {
      provide: UserApi,
      useClass: UserApi,
    },
    {
      provide: AddressApi,
      useClass: AddressApi,
    },
    {
      provide: AddressToUserApi,
      useClass: AddressToUserApi,
    },
    {
      provide: AuthApi,
      useClass: AuthApi,
    },
    // Zones et climat des adresses : un appel par adresse, partagé par les
    // cartes et leurs feuilles, le temps de l'écran.
    AddressLocationsService,
  ],
})
export class UserProfileComponent implements OnInit {
  editUserMode: boolean = false;
  languageMode: boolean = false;
  notificationPrefsMode: boolean = false;
  premiumPaywallMode: boolean = false;
  activatingPremium: boolean = false;
  subscriptionMode: boolean = false;

  readonly premiumService = inject(PremiumService);
  /** « Alertes coupées » : un refus du système se lève dans les réglages. */
  readonly pushPermission = inject(PushPermissionService);
  private readonly translate = inject(TranslateService);
  private readonly dataStore = inject(DataStore);

  /** « Membre depuis » — le premier acces obtenu, essai inclus. */
  get premiumSinceLabel(): string {
    return formatVwDate(this.premiumService.subscription()?.premiumSince, 'numeric', currentAppLang(this.translate));
  }

  /**
   * Ce qui se passe a l'echeance, et cela depend du canal : un abonnement paye
   * se renouvelle, un essai s'arrete, des mois offerts s'epuisent.
   */
  get premiumNextLabel(): string {
    const state = this.premiumService.subscription();
    if (!state?.currentPeriodEnd) return '';
    const date = formatVwDate(state.currentPeriodEnd, 'numeric', currentAppLang(this.translate));
    if (state.provider === 'TRIAL') {
      return this.translate.instant('premium.manage.trial-until', { date });
    }
    if (state.provider === 'MANUAL') {
      return this.translate.instant('premium.manage.gifted-until', { date });
    }
    if (state.cancelAtPeriodEnd) {
      return this.translate.instant('premium.manage.ends-on', { date });
    }
    return this.translate.instant('premium.manage.next-renewal', { date });
  }

  /**
   * Un abonne gere son abonnement, un non-abonne voit l'offre : deux intentions
   * distinctes derriere la meme carte.
   */
  openPremium(): void {
    if (this.premiumService.isPremium()) {
      this.subscriptionMode = true;
    } else {
      this.premiumPaywallMode = true;
    }
  }

  /** Un essai ou des mois offerts renvoient vers l'offre payante. */
  onSubscriptionToastClosed(result?: { upgrade?: boolean }): void {
    this.subscriptionMode = false;
    if (result?.upgrade) this.premiumPaywallMode = true;
  }

  onDowngradeAcknowledged(result?: { upgrade?: boolean }): void {
    if (result?.upgrade) this.premiumPaywallMode = true;
  }
  user: User | null = null;
  userAddresses: Address[] = [];
  userPictureUrl: string;
  /** Le compte et ses adresses : une panne se dit, avec « Réessayer » (13). */
  state: LoadState = LOADING;
  showConfirmDelete: boolean = false;
  /** Suppression du compte en cours : le bouton tourne, un second toucher ne renvoie rien. */
  deletingAccount = false;
  /** Suppression refusée : la feuille reste ouverte et le dit. */
  deleteAccountFailed = false;

  constructor(
    private userApi: UserApi,
    private authApi: AuthApi,
    private addressApi: AddressApi,
    private addressToUserApi: AddressToUserApi,
    private storageService: StorageService,
    private router: Router,
    private route: ActivatedRoute,
    private deviceTokenService: DeviceTokenService,
    private posthogService: PosthogService,
  ) {}

  /**
   * Lifecycle hook that is called after data-bound properties are initialized.
   * Fetches account statistics data from CompteService.
   */
  async ngOnInit() {
    void this.pushPermission.refreshStatus();
    await this.load();
    await this.handleCheckoutReturn();
  }

  get isLoading(): boolean {
    return this.state.status === 'loading';
  }

  /** Le compte puis ses adresses ; rappelé tel quel par « Réessayer ». */
  async load() {
    this.state = LOADING;
    try {
      await this.fetchUserInfo();
      await this.fetchUserAddresses();
      this.state = { status: 'ready' };
    } catch (e) {
      console.warn('UserProfileComponent: failed to load the account', e);
      this.state = errorState(e);
    }
  }

  /**
   * Retour du tunnel de paiement.
   *
   * Le paiement se conclut HORS de l'app (navigateur externe), et Stripe nous
   * previent par webhook : au moment ou l'utilisateur revient, le droit n'est
   * pas forcement encore enregistre. On attend l'activation au lieu de le
   * laisser devant un ecran inchange apres avoir paye.
   */
  private async handleCheckoutReturn(): Promise<void> {
    const checkout = this.route.snapshot.queryParamMap.get('checkout');
    if (checkout !== 'success') return;

    this.activatingPremium = true;
    try {
      // La celebration est declenchee par PremiumService, qui detecte la
      // transition gratuit -> premium : elle s'affiche donc aussi bien ici
      // qu'apres un essai lance depuis un autre ecran.
      await this.premiumService.awaitActivation();
    } finally {
      this.activatingPremium = false;
      // Nettoyer l'URL : un rafraichissement ne doit pas rejouer l'attente.
      void this.router.navigate([], {
        relativeTo: this.route,
        queryParams: {},
        replaceUrl: true,
      });
    }
  }

  /**
   * Fetches mock account statistics data from the service and assigns it to accountStats.
   */
  async fetchUserInfo() {
    this.user = await this.userApi.getCurrentUser();
    if (this.user && this.user.files && this.user.files.length > 0) {
      if (
        (this.user.files[(this.user as any).files.length - 1] as any).cdnUrl
      ) {
        this.userPictureUrl = (
          this.user.files[(this.user as any).files.length - 1] as any
        ).cdnUrl;
      }
    }
  }

  async fetchUserAddresses() {
    // Les adresses suivent le compte (fetchUserInfo) : sans compte, rien à lister.
    if (!this.user) return;
    const listAddressToUser = new AddressToUserListRequest();
    listAddressToUser.userUid = this.user.uid;
    const addressesToUser = await this.addressToUserApi.list(
      listAddressToUser,
      0,
      20,
      this.storageService.getCurrentLanguage(),
    );
    const addressesUids = addressesToUser.results.map(
      (addressToUser) => addressToUser.addressUid,
    );
    const listAddresses = new AddressListRequest();
    listAddresses.uids = addressesUids;
    const addressesResponse = await this.addressApi.list(
      listAddresses,
      0,
      20,
      this.storageService.getCurrentLanguage(),
    );
    this.userAddresses = addressesResponse.results;
  }

  /** Nom affiché (prénom, sinon pseudo), comme sur l'accueil ; à défaut, l'e-mail. */
  get displayName(): string {
    return userDisplayName(this.user) || (this.user?.email ?? '');
  }

  /** Lettre de l'avatar sans photo ; vide = icône personne, jamais un cercle vide. */
  get initial(): string {
    return userInitial(this.user);
  }

  /** « Olivier, Intermédiaire » : seulement les parties connues. */
  get infosSummary(): string {
    const expertise = this.user?.expertise
      ? this.translate.instant('profile.infos.user-types.' + this.user.expertise)
      : '';
    return [userDisplayName(this.user), expertise].filter(Boolean).join(', ');
  }

  openHandover(): void {
    this.router.navigate([RoutesConstants.GARDEN_HANDOVER]);
  }

  openFeedback(): void {
    this.router.navigate([RoutesConstants.FEEDBACK]);
  }

  /**
   * Déconnexion, désormais une ligne du groupe « Compte » (elle était cachée
   * sous la roue dentée, qui ne désinscrivait pas l'appareil des push).
   */
  async logout() {
    await this.deviceTokenService.unregisterCurrentDevice();
    this.posthogService.capture('logout');
    this.posthogService.reset();
    this.authApi.logout();
    this.clearSession();
    this.router.navigate([RoutesConstants.INIT]);
  }

  /** Jetons, invité, adresse mémorisée et données gardées hors ligne : rien ne doit survivre à la sortie. */
  private clearSession(): void {
    this.dataStore.clear();
    this.storageService.removeItem(environment.ACCESS_TOKEN);
    this.storageService.removeItem(environment.REFRESH_TOKEN);
    this.storageService.removeItem(environment.GUEST_UID);
    this.storageService.removeItem(environment.GUEST_EMAIL);
    this.storageService.removeItem('address-uid');
  }

  /** La langue du compte, sous son propre nom, comme dans sa feuille. */
  get languageName(): string {
    const language = this.user?.preferredLanguage;
    return language ? (LANGUAGE_NATIVE_NAMES[language] ?? '') : '';
  }

  /** La feuille a déjà enregistré et appliqué la langue : la ligne suit, sans recharger le compte. */
  onLanguageClosed(result?: { preferredLanguage?: string }): void {
    this.languageMode = false;
    if (result?.preferredLanguage && this.user) {
      this.user.preferredLanguage = result.preferredLanguage as Language;
    }
  }

  async onCloseEditUser($event: any) {
    await this.fetchUserInfo();
    this.editUserMode = false;
  }

  async onCloseEditAddressToast() {
    await this.fetchUserAddresses();
  }

  onDeleteAccount() {
    this.deleteAccountFailed = false;
    this.showConfirmDelete = true;
  }

  closeDeleteConfirm() {
    this.showConfirmDelete = false;
    this.deleteAccountFailed = false;
  }

  async onConfirmDelete() {
    if (this.deletingAccount) return;
    this.deletingAccount = true;
    this.deleteAccountFailed = false;
    try {
      await this.userApi.deleteUser();
    } catch (e) {
      // Suppression refusée : la confirmation reste ouverte, l'échec se voit et on peut réessayer.
      console.error('UserProfileComponent: failed to delete account', e);
      this.deleteAccountFailed = true;
      return;
    } finally {
      this.deletingAccount = false;
    }
    this.authApi.logout();
    this.clearSession();
    this.showConfirmDelete = false;
    this.router.navigate([RoutesConstants.INIT]);
  }
}
